<template>
  <main class="pillar-page font-sans">
    <article class="content-container">

      <!-- Breadcrumb (crawlable internal link + UX) -->
      <nav class="breadcrumb" aria-label="Breadcrumb">
        <router-link to="/" class="crumb">Home</router-link>
        <span class="crumb-sep">/</span>
        <span class="crumb current">Why Fee-Only SEBI RIA</span>
      </nav>

      <header class="pillar-header">
        <h1 class="main-title">Why Choosing a Fee-Only SEBI RIA Protects Your Wealth vs. Mutual Fund Distributors</h1>
        <p class="subtitle">An analytical breakdown of the conflict-free investment advisory framework in the Indian financial ecosystem.</p>
        <div class="author-meta">
          <span>By <strong>Vivek Gupta</strong> (CFA® Charterholder &amp; IIT Delhi Alumnus)</span>
          <span class="location-tag">📍 New Delhi, India</span>
        </div>
      </header>

      <section class="editorial-section">
        <h2>The Structural Conflict in Indian Wealth Management</h2>
        <p>
          Most investors in India approach a "financial advisor" expecting objective guidance aligned to their unique personal goals. However, a significant portion of the traditional wealth management industry operates on a distribution-led framework. Mutual fund distributors are compensated via hidden trail commissions embedded directly into the <strong>Regular Plans</strong> of the schemes they recommend.
        </p>
        <p>
          This compensation structure introduces an inherent, systemic conflict of interest. A distributor's revenue scales alongside your portfolio's total Assets Under Management (AUM) and the commission rates paid out by Asset Management Companies (AMCs), rather than the absolute performance or strategic suitability of your investments.
        </p>
      </section>

      <div class="inline-tool-callout">
        <h3>Simulate the Wealth Drag on Your Portfolio</h3>
        <p>A seemingly minor 1.5% commission differential compounded over a 15-year horizon can quietly erode lakhs from your future corpus.</p>
        <router-link to="/financial-calculators" class="action-link">
          Launch Expense Ratio Drag Calculator →
        </router-link>
      </div>

      <section class="editorial-section">
        <h2>What Makes a Fee-Only SEBI Registered Investment Adviser Different?</h2>
        <p>
          To safeguard retail investors, the <strong>Securities and Exchange Board of India (SEBI)</strong> introduced the strict SEBI (Investment Advisers) Regulations. Under this regulatory standard, an individual registered as a <strong>SEBI RIA (Registration No: INA000020554)</strong> operates under an absolute fiduciary mandate.
        </p>
        <p>
          This legal status enforces a rigid separation between execution and advice:
        </p>
        <ul class="semantic-list">
          <li><strong>Zero Commissions:</strong> A fee-only SEBI RIA is legally barred from receiving any backend incentives, trail fees, or commissions from AMCs or insurance providers.</li>
          <li><strong>Direct Plan Mandate:</strong> Advisers are mandated to recommend exclusively <strong>Direct Plans</strong>, ensuring your entire capital goes to work at the lowest possible expense ratios.</li>
          <li><strong>Pure Client Alignment:</strong> The adviser's sole revenue source is a transparent, flat fee paid directly by the client, ensuring complete alignment of interests.</li>
        </ul>
      </section>

      <section class="editorial-section">
        <h2>Regular Plan vs. Direct Plan: The Compounding Cost of Commissions</h2>
        <p>
          The difference between a Regular Plan and a Direct Plan is the embedded distributor commission. While a ~1% to 1.5% annual difference in expense ratio sounds trivial, compounding turns it into one of the largest silent drags on long-term wealth creation for Indian investors.
        </p>
        <p>
          Consider a ₹50,000 monthly SIP growing at 12% over 25 years. The commission leakage inside Regular Plans can translate into a shortfall of several <strong>tens of lakhs</strong> versus an equivalent Direct Plan portfolio — money that stays in your pocket when you work with a fee-only adviser.
        </p>
      </section>

      <section class="editorial-section">
        <h2>How to Verify a SEBI Registered Investment Adviser</h2>
        <p>
          Before engaging any investment adviser in India, validate their credentials directly on the official SEBI intermediary database. A genuine SEBI RIA will transparently display their registration number, validity, and BASL (BSE Administration &amp; Supervision Ltd) membership.
        </p>
        <ul class="semantic-list">
          <li><strong>Registration Number:</strong> Confirm the INA-series number (e.g., <strong>INA000020554</strong>) on the SEBI portal.</li>
          <li><strong>Fee-Only Declaration:</strong> Ensure the adviser explicitly states they accept no commissions.</li>
          <li><strong>Direct Plan Execution:</strong> Recommendations should route through Direct Plans and your own demat/folio.</li>
        </ul>
      </section>

      <section class="editorial-section local-anchor">
        <h2>Trusted, Data-Driven Wealth Guidance in Delhi NCR</h2>
        <p>
          As a <strong>fee-only SEBI Registered Investment Adviser based in New Delhi</strong>, the focus is singular: building goal-based, tax-efficient portfolios for residents across Delhi NCR, Gurugram, Noida, and clients investing in India from across the globe. There are no product sales, no commissions, and no hidden incentives — only transparent, fiduciary advice.
        </p>
        <p>
          Whether you are searching for an <strong>investment advisor in Delhi</strong>, planning long-term <strong>investment in India</strong>, or seeking a conflict-free <strong>SEBI RIA</strong> to review your existing portfolio, the advisory framework here is engineered around your financial interests alone — backed by quantitative analysis, not sales targets.
        </p>
      </section>

      <!-- Closing CTA -->
      <div class="inline-tool-callout closing-cta">
        <h3>Ready for a Conflict-Free Second Opinion?</h3>
        <p>Assess your risk profile and model your goals with transparent, fiduciary-grade tools — no login required.</p>
        <router-link to="/risk-assessment" class="action-link">
          Start Your Free Risk Assessment →
        </router-link>
      </div>

    </article>
  </main>
</template>

<script setup>
import { onMounted, onBeforeUnmount } from 'vue'

// ---------------------------------------------------------------------------
// SEO meta management (dependency-free).
// Sets <title>, meta description, canonical, Open Graph, Twitter, and JSON-LD
// structured data on mount; restores/cleans up on unmount so other routes
// are unaffected. This is the isolated, production-safe approach.
// ---------------------------------------------------------------------------

const PAGE_URL = 'https://www.assetnivesh.com/why-fee-only-sebi-ria'
const PAGE_TITLE = 'Why a Fee-Only SEBI RIA Protects Your Wealth | Investment Advisor in Delhi'
const PAGE_DESC = 'Discover why a fee-only SEBI Registered Investment Adviser (RIA) in Delhi offers conflict-free, fiduciary advice vs. commission-based mutual fund distributors. Data-driven guidance for investment in India.'

const createdNodes = []
let previousTitle = ''

function setMetaTag(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`)
  if (el) {
    // Preserve and restore existing tags rather than destroying them.
    if (!el.dataset._anPrev) el.dataset._anPrev = el.getAttribute('content') || ''
    el.setAttribute('content', content)
  } else {
    el = document.createElement('meta')
    el.setAttribute(attr, key)
    el.setAttribute('content', content)
    document.head.appendChild(el)
    createdNodes.push(el)
  }
}

function setLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`)
  if (el) {
    if (!el.dataset._anPrev) el.dataset._anPrev = el.getAttribute('href') || ''
    el.setAttribute('href', href)
  } else {
    el = document.createElement('link')
    el.setAttribute('rel', rel)
    el.setAttribute('href', href)
    document.head.appendChild(el)
    createdNodes.push(el)
  }
}

onMounted(() => {
  previousTitle = document.title
  document.title = PAGE_TITLE

  // Standard SEO
  setMetaTag('name', 'description', PAGE_DESC)
  setMetaTag('name', 'keywords', 'SEBI RIA, Investment Advisor in Delhi, Investment in India, Fee-Only Financial Advisor, SEBI Registered Investment Adviser, Direct Plan, Mutual Fund Distributor')
  setMetaTag('name', 'robots', 'index, follow, max-image-preview:large')
  setLink('canonical', PAGE_URL)

  // Open Graph
  setMetaTag('property', 'og:type', 'article')
  setMetaTag('property', 'og:title', PAGE_TITLE)
  setMetaTag('property', 'og:description', PAGE_DESC)
  setMetaTag('property', 'og:url', PAGE_URL)
  setMetaTag('property', 'og:site_name', 'Asset Nivesh')
  setMetaTag('property', 'og:image', 'https://www.assetnivesh.com/android-chrome-512x512.png')

  // Twitter
  setMetaTag('name', 'twitter:card', 'summary_large_image')
  setMetaTag('name', 'twitter:title', PAGE_TITLE)
  setMetaTag('name', 'twitter:description', PAGE_DESC)

  // JSON-LD structured data (the part LLM/AI engines & Google value most)
  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'FinancialService',
        '@id': 'https://www.assetnivesh.com/#organization',
        name: 'Asset Nivesh — Vivek Gupta (SEBI RIA)',
        url: 'https://www.assetnivesh.com/',
        description: PAGE_DESC,
        areaServed: ['Delhi', 'Delhi NCR', 'Gurugram', 'Noida', 'India'],
        address: {
          '@type': 'PostalAddress',
          streetAddress: '65, RPS, Sheikh Sarai, Phase I',
          addressLocality: 'New Delhi',
          addressRegion: 'Delhi',
          postalCode: '110017',
          addressCountry: 'IN'
        },
        founder: {
          '@type': 'Person',
          name: 'Vivek Gupta',
          jobTitle: 'SEBI Registered Investment Adviser (RIA)',
          alumniOf: 'Indian Institute of Technology (IIT) Delhi',
          hasCredential: 'CFA® Charterholder',
          identifier: 'SEBI Reg. No. INA000020554'
        }
      },
      {
        '@type': 'Article',
        '@id': PAGE_URL + '#article',
        headline: 'Why Choosing a Fee-Only SEBI RIA Protects Your Wealth vs. Mutual Fund Distributors',
        description: PAGE_DESC,
        mainEntityOfPage: PAGE_URL,
        author: {
          '@type': 'Person',
          name: 'Vivek Gupta',
          hasCredential: 'CFA® Charterholder'
        },
        publisher: { '@id': 'https://www.assetnivesh.com/#organization' },
        about: ['SEBI RIA', 'Fee-Only Financial Advisor', 'Investment Advisor in Delhi', 'Investment in India']
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.assetnivesh.com/' },
          { '@type': 'ListItem', position: 2, name: 'Why Fee-Only SEBI RIA', item: PAGE_URL }
        ]
      }
    ]
  }

  const script = document.createElement('script')
  script.type = 'application/ld+json'
  script.text = JSON.stringify(jsonLd)
  script.dataset._anJsonld = 'why-fee-only-sebi-ria'
  document.head.appendChild(script)
  createdNodes.push(script)
})

onBeforeUnmount(() => {
  // Restore title
  if (previousTitle) document.title = previousTitle

  // Remove nodes we created
  createdNodes.forEach((el) => el.parentNode && el.parentNode.removeChild(el))
  createdNodes.length = 0

  // Restore tags we mutated
  document.head.querySelectorAll('[data-_an-prev], [data-_anPrev]').forEach((el) => {
    const prev = el.dataset._anPrev
    if (prev !== undefined) {
      if (el.tagName === 'LINK') el.setAttribute('href', prev)
      else el.setAttribute('content', prev)
      delete el.dataset._anPrev
    }
  })
})
</script>

<style scoped>
.pillar-page {
  padding: 60px 24px;
  background-color: #ffffff;
  color: #374151;
  line-height: 1.75;
}
.content-container {
  max-width: 800px;
  margin: 0 auto;
}
.breadcrumb {
  font-size: 0.85rem;
  color: #6b7280;
  margin-bottom: 24px;
}
.breadcrumb .crumb {
  color: #0284c7;
  text-decoration: none;
}
.breadcrumb .crumb:hover {
  text-decoration: underline;
}
.breadcrumb .crumb.current {
  color: #6b7280;
}
.breadcrumb .crumb-sep {
  margin: 0 8px;
  color: #d1d5db;
}
.pillar-header {
  margin-bottom: 40px;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 24px;
}
.main-title {
  font-size: 2.5rem;
  font-weight: 800;
  color: #111827;
  line-height: 1.2;
  margin-bottom: 16px;
}
.subtitle {
  font-size: 1.2rem;
  color: #4b5563;
  margin-bottom: 16px;
}
.author-meta {
  display: flex;
  justify-content: space-between;
  font-size: 0.875rem;
  color: #6b7280;
}
.editorial-section {
  margin-bottom: 32px;
}
.editorial-section h2 {
  font-size: 1.5rem;
  font-weight: 700;
  color: #111827;
  margin-top: 32px;
  margin-bottom: 16px;
}
.editorial-section p {
  margin-bottom: 16px;
  font-size: 1.05rem;
}
.semantic-list {
  padding-left: 24px;
  margin-bottom: 24px;
  list-style-type: disc;
}
.semantic-list li {
  margin-bottom: 8px;
  font-size: 1.05rem;
}
.inline-tool-callout {
  background-color: #f0f9ff;
  border-left: 4px solid #0284c7;
  padding: 20px;
  border-radius: 0 8px 8px 0;
  margin: 32px 0;
}
.inline-tool-callout h3 {
  font-weight: 700;
  color: #0369a1;
  margin: 0 0 8px 0;
}
.inline-tool-callout p {
  margin: 0 0 16px 0;
  font-size: 0.95rem;
}
.closing-cta {
  background-color: #f0fdf4;
  border-left-color: #16a34a;
}
.closing-cta h3 {
  color: #15803d;
}
.action-link {
  font-weight: 700;
  color: #0284c7;
  text-decoration: none;
}
.action-link:hover {
  text-decoration: underline;
}
.local-anchor {
  background-color: #f9fafb;
  padding: 24px;
  border-radius: 8px;
  border: 1px solid #f3f4f6;
}
@media (max-width: 768px) {
  .main-title { font-size: 2rem; }
  .author-meta { flex-direction: column; gap: 8px; }
}
</style>
